import React, { useState, useEffect, useCallback, useMemo } from 'react'
import sensors, { parsePlaceValue, sensorsRole } from '../../../utils/sensors'
import { fetchWatermarkList } from '../../../actions/photo'
import { Modal, Tabs, Empty, Input } from 'antd'

import './index.scss'
import { capturePromiseException } from '../../../utils/sentryUtils'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { IWatermarksPickerProps, Watermark } from './typings'
import WatermarkItem from './components/WatermarkItem'
import actionTypes from '../../../constants/actionTypes'

const { Search } = Input

enum WatermarkType {
  Group = 1,
  Normal = 2,
  NoWatermark = 3
}

const WatermarksPicker: React.FC<IWatermarksPickerProps> = (props) => {
  const { visible, checkedList, onlyInGroup = false, onCancel, onOK } = props
  const groupID = useAppSelector((state) => state.group.groupId)
  const dispatch = useAppDispatch()
  const [watermarks, setWatermarks] = useState<Watermark[]>([])
  const [checkedWatermarks, setCheckedWatermarks] = useState<string[]>([])
  const [allSearchValue, setAllSearchValue] = useState<string>('')
  const [groupSearchValue, setGroupSearchValue] = useState<string>('')
  const [normalSearchValue, setNormalSearchValue] = useState<string>('')
  const myRole = useAppSelector((state) => state.group.myRole)
  const allWatermarks = useMemo(() => {
    return watermarks.filter(
      (watermark) => watermark.name.indexOf(allSearchValue) > -1
    )
  }, [watermarks, allSearchValue])

  const normalWatermarks = useMemo(() => {
    return watermarks.filter(
      (watermark) =>
        watermark.type === WatermarkType.Normal &&
        watermark.name.indexOf(normalSearchValue) > -1
    )
  }, [watermarks, normalSearchValue])

  const groupWatermarks = useMemo(() => {
    return watermarks.filter(
      (watermark) =>
        watermark.type === WatermarkType.Group &&
        watermark.name.indexOf(groupSearchValue) > -1
    )
  }, [watermarks, groupSearchValue])

  useEffect(() => {
    if (!visible) {
      setAllSearchValue('')
      setGroupSearchValue('')
      setNormalSearchValue('')
    }
  }, [visible])

  useEffect(() => {
    dispatch(fetchWatermarkList({ groupID }))
      .then((data) => {
        setWatermarks(data)
        dispatch({
          type: actionTypes.SET_WATERMARKS_NUM,
          data: data.length
        })
      })
      .catch(capturePromiseException)
  }, [dispatch, groupID])

  useEffect(() => {
    if (Array.isArray(checkedList)) {
      setCheckedWatermarks(checkedList)
    } else {
      setCheckedWatermarks([])
    }
  }, [checkedList])

  const onCheckedChange = (value) => setCheckedWatermarks(value)

  const onOkClick = useCallback(() => {
    sensors.track('web_click_pop_workgroup_filter_watermark', {
      clickItem: 'confirm',
      pageType: parsePlaceValue(),
      groupID,
      role: sensorsRole(myRole)
    })
    const ids = checkedWatermarks.map((id) => id.split('+'))
    sensors.track('web_workgroup_filter_watermark_page_click_done', {
      watermarkNum:
        checkedWatermarks.length === watermarks.length
          ? 0
          : checkedWatermarks.length,
      groupWatermarkNum: ids.filter((id) => id[1]).length,
      basicWatermarkNum: ids.filter((id) => id[0] !== '0' && id[1] === '')
        .length,
      noWatermarkNum: ids.filter((id) => id[0] === '0').length
    })
    if (checkedWatermarks.length === watermarks.length) {
      onOK([])
    } else {
      onOK(checkedWatermarks)
    }
  }, [checkedWatermarks, watermarks, groupID, myRole, onOK])

  const onCancelClick = useCallback(() => {
    sensors.track('web_click_pop_workgroup_filter_watermark', {
      clickItem: 'cancel',
      pageType: parsePlaceValue(),
      groupID,
      role: sensorsRole(myRole)
    })
    onCancel()
  }, [groupID, myRole, onCancel])

  const onTabsChange = useCallback(
    (activeKey) => {
      let clickItem = ''
      switch (activeKey) {
        case 'all':
          clickItem = 'allWatermark'
          break
        case 'group':
          clickItem = 'groupWatermark'
          break
        case 'normal':
          clickItem = 'ordinaryWatermark'
      }
      sensors.track('web_click_pop_workgroup_filter_watermark', {
        clickItem,
        pageType: parsePlaceValue(),
        groupID,
        role: sensorsRole(myRole)
      })
    },
    [groupID, myRole]
  )

  return (
    <Modal
      title="选择水印"
      wrapClassName="watermarks-picker-modal"
      destroyOnHidden
      open={visible}
      width={720}
      onCancel={onCancelClick}
      onOk={onOkClick}
    >
      <Tabs
        defaultActiveKey="all"
        tabPosition="left"
        onChange={onTabsChange}
        items={[
          ...(onlyInGroup
            ? []
            : [
                {
                  key: 'all',
                  label: '全部水印',
                  style: { width: 570 },
                  children: (
                    <>
                      <Search
                        placeholder="输入水印名称搜索"
                        onSearch={(value) => setAllSearchValue(value)}
                        enterButton
                      />
                      <div className="watermarks-list">
                        {allWatermarks.map((watermark, index) => (
                          <WatermarkItem
                            key={index}
                            checkedWatermarks={checkedWatermarks}
                            watermark={watermark}
                            onCheck={onCheckedChange}
                          />
                        ))}
                        {allWatermarks.length === 0 && (
                          <Empty description="列表为空" />
                        )}
                      </div>
                    </>
                  )
                }
              ]),
          {
            key: 'group',
            label: '团队水印',
            style: { width: 570 },
            children: (
              <>
                <Search
                  placeholder="输入水印名称搜索"
                  onSearch={(value) => setGroupSearchValue(value)}
                  enterButton
                />
                <div className="watermarks-list">
                  {groupWatermarks.map((watermark, index) => (
                    <WatermarkItem
                      key={index}
                      checkedWatermarks={checkedWatermarks}
                      watermark={watermark}
                      onCheck={onCheckedChange}
                    />
                  ))}
                  {groupWatermarks.length === 0 && (
                    <Empty description="列表为空" />
                  )}
                </div>
              </>
            )
          },
          ...(onlyInGroup
            ? []
            : [
                {
                  key: 'normal',
                  label: '普通水印',
                  style: { width: 570 },
                  children: (
                    <>
                      <Search
                        placeholder="输入水印名称搜索"
                        onSearch={(value) => setNormalSearchValue(value)}
                        enterButton
                      />
                      <div className="watermarks-list">
                        {normalWatermarks.map((watermark, index) => (
                          <WatermarkItem
                            key={index}
                            checkedWatermarks={checkedWatermarks}
                            watermark={watermark}
                            onCheck={onCheckedChange}
                          />
                        ))}
                        {normalWatermarks.length === 0 && (
                          <Empty description="列表为空" />
                        )}
                      </div>
                    </>
                  )
                }
              ])
        ]}
      />
    </Modal>
  )
}

export default WatermarksPicker
